import { useState, useEffect, useContext } from 'react';
import {
  MsalAuthenticationTemplate,
  useMsal,
  useAccount,
} from '@azure/msal-react';
import { InteractionStatus, InteractionType } from '@azure/msal-browser';
import { Avatar, ThemeProvider } from '@mui/material';
import { loginRequest } from '../helpers/auth-config';
import { callMsGraph, getPhoto, getGroups } from '../helpers/ms-graph-api';
import UserContext from '../context/user-context';
import { P21UserProvider } from '../context/p21-user-context';
import { legacyTheme, GridContainer, GridItem } from './shared/ui-extension';
import { colors } from '../shared/styles';
import { dateFormats, formatDateTime } from '../shared/utils';
import {
  defaultWindowHeight,
  Notification,
  useNotificationContext,
} from './shared/notification';
import { HeaderMenu } from './header-menu';

const isFramed = () => {
  if (typeof window === 'undefined') return false;
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};

const SiteLayout = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const { windowHeight } = useNotificationContext();

  const [framed] = useState(isFramed);

  const account = useAccount(accounts[0] || {});
  const { dispatch } = useContext(UserContext);

  const [graphData, setGraphData] = useState(null);
  const [profilePic, setProfilePic] = useState('');

  const [graphDataLoaded, setGraphDataLoaded] = useState(false);

  useEffect(() => {
    if (
      !graphDataLoaded &&
      account &&
      inProgress !== InteractionStatus.Startup
    ) {
      requestProfileData();
    }
  }, [account, inProgress]);

  const requestProfileData = async () => {
    setGraphDataLoaded(true);
    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: account,
      });

      const {
        preferred_username: userEmail,
        name,
        oid: objectId,
      } = response?.idTokenClaims || {};

      const graphResponse = await callMsGraph(response.accessToken);
      setGraphData(graphResponse);

      const groupResponse = await getGroups(response.accessToken);
      dispatch({
        type: 'SET_GROUPS',
        payload: {
          groups: groupResponse?.value || [],
          user: {
            emailAddress: userEmail?.toLowerCase(),
            name: name,
            objectId: objectId,
          },
        },
      });

      const photoResponse = await getPhoto(response.accessToken);
      const blob = await photoResponse.blob();
      const imageUrl = URL.createObjectURL(blob);
      setProfilePic(imageUrl);

      if (userEmail) {
        localStorage.setItem('userEmail', `${userEmail}`.toLowerCase());
        localStorage.setItem('username', `${name}`);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const ErrorComponent = ({ error }) => {
    return <div>An Error Occurred: {error.errorCode}</div>;
  };

  const Loading = () => {
    return <div>Authentication in progress...</div>;
  };

  const authRequest = {
    ...loginRequest,
  };

  const ProfilePicture = graphData ? (
    <Avatar
      title={`${graphData.displayName}\n${graphData?.userPrincipalName}`}
      src={profilePic}
      sx={{ width: 48, height: 48 }}
      alt={
        graphData.displayName
          ?.split(/\s/)
          ?.map((m) => m[0]?.toUpperCase())
          .join('') || 'PAM'
      }
    />
  ) : null;

  return (
    <MsalAuthenticationTemplate
      interactionType={
        framed ? InteractionType.Popup : InteractionType.Redirect
      }
      authenticationRequest={authRequest}
      errorComponent={ErrorComponent}
      loadingComponent={Loading}
    >
      <ThemeProvider theme={legacyTheme}>
        <P21UserProvider graphData={graphData}>
          <Notification>
            <GridContainer>
              {!framed && (
                <GridItem xs={12}>
                  <GridContainer
                    justifyContent='space-between'
                    alignItems='center'
                    sx={{
                      backgroundColor: colors.blue,
                      padding: '0 24px 8px 8px',
                      marginTop: '0',
                    }}
                  >
                    <GridItem>
                      <HeaderMenu />
                    </GridItem>
                    <GridItem>{ProfilePicture}</GridItem>
                  </GridContainer>
                </GridItem>
              )}
              <GridItem
                xs={12}
                sx={{
                  padding: '28px 24px 8px 32px !important',
                  minHeight: (windowHeight || defaultWindowHeight) - 170,
                  background: '#fff',
                }}
              >
                {children}
              </GridItem>
              <GridItem
                xs={12}
                sx={{
                  textAlign: 'center',
                  paddingBottom: '8px',
                  fontSize: '12px',
                  backgroundColor: 'transparent',
                }}
              >
                Container and Packaging ©
                {formatDateTime(new Date(), dateFormats.year)}
                {` - Build [${process.env.NEXT_PUBLIC_BUILD_ID || 'LOCAL'}]`}
              </GridItem>
            </GridContainer>
          </Notification>
        </P21UserProvider>
      </ThemeProvider>
    </MsalAuthenticationTemplate>
  );
};

export default SiteLayout;
